/**
 * FoodPulseHealth — `/food-pulse/health` landing + `/food-pulse/health/:topic`
 *
 * The Health section dropdown lists 10 topics (Nutrition, Healthy Eating,
 * Weight Management, Diabetes, Heart Health, Children's Nutrition,
 * Senior Nutrition, Food Allergies, Food Safety, Supplements) plus an
 * "All of Health" hub landing. Each maps 1:1 to a food-article seeded by
 * `seed_health_articles.py` with category="health" and tag=<topic-slug>.
 *
 * This single component handles both the landing and the article view:
 *   - No `:topic` param → show the "All of Health" cover article + a grid of
 *     the other 10 topic cards.
 *   - `:topic` param   → fetch that one article by tag and render it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, HeartPulse, Sparkles, Utensils, Scale, Droplet, Baby, Users, ShieldAlert, Refrigerator, Pill } from "lucide-react";

import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

// Topic sub-nav — mirrors the Health children in foodPulseSections.js.
// `tag` is the DB field on food_articles used to fetch this topic.
const HEALTH_TOPICS = [
  { slug: "",                     tag: "all",                 label: "All of Health",       icon: HeartPulse,   blurb: "The complete FoodPulse guide to evidence-based nutrition, hidden across ten topic articles." },
  { slug: "nutrition",            tag: "nutrition",           label: "Nutrition",           icon: Sparkles,     blurb: "The science of macros, micros, and how your body actually uses food." },
  { slug: "healthy-eating",       tag: "healthy-eating",      label: "Healthy Eating",      icon: Utensils,     blurb: "The half-plate rule, mindful eating, and simple daily habits." },
  { slug: "weight-management",    tag: "weight-management",   label: "Weight Management",   icon: Scale,        blurb: "Sustainable balance — energy, hormones, sleep, and stress." },
  { slug: "diabetes",             tag: "diabetes",            label: "Diabetes",            icon: Droplet,      blurb: "Types, dietary strategies, and lifestyle pillars for blood sugar control." },
  { slug: "heart-health",         tag: "heart-health",        label: "Heart Health",        icon: HeartPulse,   blurb: "Four dietary pillars for cardiovascular wellness and steady blood pressure." },
  { slug: "childrens-nutrition",  tag: "childrens-nutrition", label: "Children's Nutrition",icon: Baby,         blurb: "Growing bodies, the Division of Responsibility, and happy mealtimes." },
  { slug: "senior-nutrition",     tag: "senior-nutrition",    label: "Senior Nutrition",    icon: Users,        blurb: "Nutrient density for aging bodies and practical mealtime tips." },
  { slug: "allergies",            tag: "allergies",           label: "Food Allergies",      icon: ShieldAlert,  blurb: "The Big 9 allergens plus four pillars of safe management." },
  { slug: "safety",               tag: "safety",              label: "Food Safety",         icon: Refrigerator, blurb: "Clean, Separate, Cook, Chill — plus safe internal temperatures." },
  { slug: "supplements",          tag: "supplements",         label: "Supplements",         icon: Pill,         blurb: "Who genuinely needs them, quality flags, and the 'food first' principle." },
  // Drill-down health topics (specific conditions & focus areas)
  { slug: "cholesterol",          tag: "cholesterol",         label: "Cholesterol",         icon: HeartPulse,   blurb: "LDL vs. HDL vs. triglycerides — the foods clinically shown to move each number." },
  { slug: "blood-pressure",       tag: "blood-pressure",      label: "Blood Pressure",      icon: HeartPulse,   blurb: "The DASH pattern, the potassium-first approach, and the numbers that matter." },
  { slug: "gut-health",           tag: "gut-health",          label: "Gut Health",          icon: Sparkles,     blurb: "The microbiome playbook — 30 plants a week, fermented foods, and what to avoid." },
  { slug: "sleep-nutrition",      tag: "sleep-nutrition",     label: "Sleep Nutrition",     icon: Sparkles,     blurb: "How diet shapes sleep quality — tryptophan, magnesium, timing, and the caffeine cutoff." },
];

// Extra search phrases per topic — used to surface Ghost/news articles
// that are on-topic but not tagged category=health. Multi-word queries
// hit the `$or` on {title, excerpt, tag} so a phrase like "blood pressure"
// still matches "Blood Pressure: The Silent Number".
const HEALTH_TOPIC_KEYWORDS = {
  all:                   ["health", "nutrition", "wellness", "diet"],
  nutrition:             ["nutrition", "vitamin", "mineral", "macro", "protein"],
  "healthy-eating":      ["healthy eating", "balanced diet", "meal plan", "mindful", "whole food"],
  "weight-management":   ["weight", "obesity", "calorie", "intermittent", "fasting"],
  diabetes:              ["diabetes", "blood sugar", "insulin", "glycemic", "diabetic"],
  "heart-health":        ["heart", "cardiovascular", "cardiac", "cholesterol", "artery"],
  "childrens-nutrition": ["children", "kids", "toddler", "school lunch", "infant"],
  "senior-nutrition":    ["senior", "elderly", "aging", "geriatric"],
  allergies:             ["allergy", "allergen", "intolerance", "gluten"],
  safety:                ["food safety", "foodborne", "contamination", "recall", "additive", "chemical", "pesticide", "preservative"],
  supplements:           ["supplement", "vitamin", "mineral", "herbal", "probiotic"],
  cholesterol:           ["cholesterol", "LDL", "HDL", "triglyceride", "statin"],
  "blood-pressure":      ["blood pressure", "hypertension", "DASH", "sodium", "salt"],
  "gut-health":          ["gut", "microbiome", "probiotic", "prebiotic", "fibre", "fiber", "fermented"],
  "sleep-nutrition":     ["sleep", "melatonin", "insomnia", "circadian"],
};

function topicBySlug(slug) {
  return HEALTH_TOPICS.find((t) => t.slug === (slug || "")) || null;
}

export default function FoodPulseHealth() {
  const { topic: topicSlug } = useParams();
  const topic = useMemo(() => topicBySlug(topicSlug), [topicSlug]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!topic) { setLoading(false); return; }
    document.title = `${topic.label} — Food Pulse Health`;
    let cancelled = false;
    setLoading(true);
    setArticle(null);
    setRelated([]);
    (async () => {
      try {
        // Step 1 — find the article slug for this health topic. The list
        // endpoint returns lightweight cards (no body), so we need step 2.
        const list = await axios.get(`${API}/api/food-articles`, {
          params: { category: "health", tag: topic.tag, limit: 1 },
        });
        const card = (list.data.items || [])[0];
        if (!card) { if (!cancelled) setArticle(null); return; }
        // Step 2 — fetch the full article by slug (includes body_html)
        const detail = await axios.get(`${API}/api/food-articles/${card.slug}`);
        if (!cancelled) setArticle(detail.data);
      } catch {
        if (!cancelled) setArticle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Related articles — hit the search endpoint per keyword, dedupe,
    // and cap at 24. Surfaces Ghost/news posts that are on-topic but
    // tagged elsewhere (e.g. "Kerala Health Paradox" → Nutrition).
    //
    // We tighten the server-side `q=` search by only keeping results
    // where the keyword appears in the TITLE or TAG. Otherwise a passing
    // mention in a food-item excerpt (e.g. "whole grains support heart
    // health") turns every grain article into a Heart Health match.
    (async () => {
      const EDITORIAL_CATS = new Set(["health", "news", "food-waste"]);
      const keywords = HEALTH_TOPIC_KEYWORDS[topic.tag] || [topic.tag.replace(/-/g, " ")];
      const seen = new Set();
      const hits = [];
      try {
        for (const kw of keywords) {
          const kwLc = kw.toLowerCase();
          const res = await axios.get(`${API}/api/food-articles`, { params: { q: kw, limit: 40 } });
          for (const it of res.data.items || []) {
            if (!EDITORIAL_CATS.has(it.category)) continue;
            if (it.category === "health" && it.tag === topic.tag) continue;
            if (/(-all|^community-|^deals-guide-|^food-waste-|^discover-|^farming-|^places-|^marketplace-|^news-food-industry|^news-agriculture|^news-nutrition-research|^news-restaurant|^news-regulations|^news-recalls|^news-sustainability|^news-technology)$/.test(it.slug))
              continue;
            // Tight match — title or tag must contain the keyword
            const title = (it.title || "").toLowerCase();
            const tag   = (it.tag   || "").toLowerCase();
            if (!title.includes(kwLc) && !tag.includes(kwLc)) continue;
            if (seen.has(it.slug)) continue;
            seen.add(it.slug);
            hits.push(it);
            if (hits.length >= 24) break;
          }
          if (hits.length >= 24) break;
        }
        if (!cancelled) setRelated(hits);
      } catch {
        if (!cancelled) setRelated([]);
      }
    })();
    return () => { cancelled = true; };
  }, [topic]);

  if (!topic) {
    // Unknown /:topic — send back to the hub.
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF7F0" }} data-testid="fp-health-unknown">
        <div className="text-center">
          <div className="text-lg font-display mb-2">Topic not found.</div>
          <Link to="/food-pulse/health" className="text-sm underline" style={{ color: "#8B5E10" }}>
            Back to Health hub →
          </Link>
        </div>
      </div>
    );
  }

  const isHub = topic.slug === "";
  const others = HEALTH_TOPICS.filter((t) => t.slug !== topic.slug && t.slug !== "");

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid={`fp-health-${topic.slug || "hub"}`}>
      <SeoHead
        section="food-pulse"
        title={`${topic.label} — Food Pulse Health`}
        description={topic.blurb}
      />

      {/* Hero */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="fp-health-hero">
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <Link
            to={isHub ? "/food-pulse" : "/food-pulse/health"}
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest mb-3 hover:underline"
            style={{ color: "#8B5E10" }}
            data-testid="fp-health-back"
          >
            <ArrowLeft className="w-3 h-3" />
            {isHub ? "Food Pulse" : "Food Pulse · Health"}
          </Link>
          <div className="flex items-start gap-4">
            <div
              className="rounded-2xl p-3"
              style={{ background: "#fff", border: "1px solid #E9D8B4" }}
              aria-hidden="true"
            >
              <topic.icon className="w-6 h-6" style={{ color: "#B25D38" }} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#B25D38" }}>
                Food Pulse · Health
              </div>
              <h1 className="font-display text-3xl md:text-5xl leading-tight mt-1" style={{ color: "#4A2E0F" }}>
                {topic.label}
              </h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base" style={{ color: "#6B4A16" }}>
                {topic.blurb}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="fp-health-loading">Loading article…</div>
        ) : !article ? (
          <div
            className="rounded-2xl border p-10 text-center"
            style={{ borderColor: "#C79A3F", background: "#fff" }}
            data-testid="fp-health-empty"
          >
            <HeartPulse className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
            <div className="font-display text-lg mb-1">Article coming soon.</div>
            <div className="text-sm opacity-70">
              We're preparing this topic. In the meantime, browse the other Health articles below.
            </div>
          </div>
        ) : (
          <article
            className="prose prose-lg max-w-none"
            style={{ color: "#3E2A0A" }}
            data-testid="fp-health-article-body"
            dangerouslySetInnerHTML={{ __html: article.body_html }}
          />
        )}
      </section>

      {/* Related articles — surfaces on-topic Ghost/news pieces even
          when they are tagged category=news rather than category=health. */}
      {!loading && related.length === 0 && article && (
        <section className="max-w-4xl mx-auto px-4 pb-10" data-testid="fp-health-related-empty">
          <div className="rounded-2xl border p-5 bg-white text-sm"
               style={{ borderColor: "#E9D8B4", color: "#3E2A0A" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>
              — More on {topic.label}
            </div>
            No additional articles matched this topic yet. New posts tagged with
            {" "}<em>{topic.label.toLowerCase()}</em>{" "}
            (or any of these keywords: {(HEALTH_TOPIC_KEYWORDS[topic.tag] || []).slice(0, 5).join(", ")})
            will show up here automatically.
          </div>
        </section>
      )}
      {related.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-10" data-testid="fp-health-related">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: "#B25D38" }}>
            — {related.length} article{related.length === 1 ? "" : "s"} on {topic.label}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/food-pulse/articles/${r.slug}`}
                className="p-4 rounded-lg border bg-white hover:shadow-md transition-all hover:-translate-y-0.5 flex flex-col gap-2"
                style={{ borderColor: "#E9D8B4" }}
                data-testid={`fp-health-related-${r.slug}`}
              >
                {r.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden rounded -mx-4 -mt-4 mb-1 bg-black/5">
                    <img src={r.cover_image} alt={r.title}
                         className="w-full h-full object-cover"
                         loading="lazy" />
                  </div>
                )}
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                  {r.category?.replace("-", " ")}
                  {r.tag && ` · ${r.tag}`}
                </div>
                <div className="font-display text-base leading-tight line-clamp-2" style={{ color: "#1A1613" }}>
                  {r.title}
                </div>
                {r.excerpt && (
                  <div className="text-xs opacity-70 line-clamp-3">{r.excerpt}</div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Other topics — always visible so readers can hop across the Health hub */}
      <section className="max-w-4xl mx-auto px-4 pb-16" data-testid="fp-health-siblings">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: "#B25D38" }}>
          — More on Health
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(isHub ? HEALTH_TOPICS.slice(1) : others).map((t) => (
            <Link
              key={t.slug || "all"}
              to={t.slug ? `/food-pulse/health/${t.slug}` : "/food-pulse/health"}
              className="p-4 rounded-lg border hover:shadow-md transition-all hover:-translate-y-0.5"
              style={{ background: "#fff", borderColor: "#E9D8B4" }}
              data-testid={`fp-health-sibling-${t.slug || "all"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <t.icon className="w-4 h-4" style={{ color: "#B25D38" }} />
                <span className="font-display text-base">{t.label}</span>
              </div>
              <div className="text-xs opacity-70">{t.blurb}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
